import React from "react";
import { CheckCircle2, Star, MapPin } from "lucide-react";
import { motion } from "framer-motion";

export default function HeroSection({ handleScroll, setShowSignupModal }) {
  return (
    <section
      id="home"
      className="pt-24 sm:pt-32 pb-16 sm:pb-24 bg-white relative overflow-hidden flex flex-col justify-center min-h-[90vh]"
    >
      {/* Very subtle blue gradient background in top right similar to mockup */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-50/50 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
          
          {/* Left Text Content */}
          <div className="lg:w-1/2 flex flex-col items-center text-center lg:items-start lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="text-[11px] font-bold uppercase tracking-widest text-primary mb-4 block">
                THE FUTURE OF VEHICLE CARE
              </span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tighter leading-[1.1] text-slate-900 mb-6"
            >
              Smart Car Care <br />
              <span className="text-primary">Made Simple &</span> <br />
              <span className="text-primary">Effortless</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base sm:text-lg text-slate-600 max-w-lg leading-relaxed font-medium mb-8"
            >
              Track maintenance, find trusted service providers, and manage your
              vehicle's health with our powerful, all-in-one automotive platform.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 mb-10 w-full sm:w-auto"
            >
              <button
                onClick={() => handleScroll("contact")}
                className="bg-primary hover:bg-blue-600 text-white px-8 py-3.5 rounded-lg font-medium transition-colors w-full sm:w-auto"
              >
                Download App Free
              </button>
              <button
                onClick={() => setShowSignupModal(true)}
                className="bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 px-8 py-3.5 rounded-lg font-medium transition-all w-full sm:w-auto"
              >
                Sign Up
              </button>
            </motion.div>

            {/* Checkmarks */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 sm:gap-8 text-sm font-medium text-slate-600"
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary fill-primary/10" />
                <span>Trusted by <br/> Drivers</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-primary fill-primary" />
                <span>4.8/5 <br/> App Store Rating</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded bg-primary/10 flex items-center justify-center text-primary">
                   <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span>Secure & <br/> Private</span>
              </div>
            </motion.div>
          </div>

          {/* Right Image Content */}
          <div className="lg:w-1/2 w-full flex justify-center lg:justify-end relative mt-10 lg:mt-0">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative w-full max-w-[320px] mx-auto lg:mx-0 lg:mr-10"
            >
              {/* Main Phone Image */}
              <div className="bg-slate-100 rounded-[2.5rem] p-3 shadow-2xl relative z-10 border border-slate-200/50">
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/americar-app.firebasestorage.app/o/1-Log%20in.png?alt=media&token=4e71101b-585b-4601-95fc-f051c8c2a392"
                  alt="App Interface"
                  className="rounded-[2rem] w-full h-[600px] object-cover"
                />
              </div>

              {/* Floating Widget 1: Vehicle Health (Top Right) */}
              <motion.div 
                animate={{ y: ["-5px", "5px"] }}
                transition={{ duration: 3, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                className="absolute -right-16 top-[15%] z-20 bg-white rounded-xl p-4 shadow-xl border border-slate-100 flex flex-col gap-2 min-w-[140px]"
              >
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wide">Vehicle Health</span>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full border-4 border-green-500 flex items-center justify-center">
                    <span className="text-xs font-bold text-slate-800">92%</span>
                  </div>
                  <div className="flex flex-col gap-1 text-[10px] font-bold text-slate-600">
                    <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-green-500"></div> Engine</div>
                    <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-green-500"></div> Brakes</div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Widget 2: Alert (Mid Left) */}
              <motion.div 
                animate={{ y: ["5px", "-5px"] }}
                transition={{ duration: 4, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                className="absolute -left-20 top-[60%] z-20 bg-white rounded-xl p-4 shadow-xl border border-slate-100 flex items-start gap-3"
              >
                <div className="w-6 h-6 rounded bg-red-50 flex items-center justify-center text-red-500 mt-0.5">
                   <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold text-red-500">1 Active Alert</span>
                  <span className="text-sm font-bold text-slate-900 leading-tight mb-1">Recall: Airbag Inflator</span>
                  <span className="text-[10px] text-primary font-medium cursor-pointer">View details &gt;</span>
                </div>
              </motion.div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
}
